import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './api/auth.service';
import { NotificationService } from './notification.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private timeoutId: any;
  private warningTimeoutId: any;
  private countdownIntervalId: any;
  private readonly inactivityTimeout = 5 * 60 * 1000;
  private readonly warningDuration = 60 * 1000;

  private readonly events = [
    'mousemove',
    'mousedown',
    'keypress',
    'touchstart',
    'scroll',
    'wheel',
    'pointermove',
    'pointerdown',
    'click',
    'contextmenu'
  ];

  private showWarningSubject = new BehaviorSubject<boolean>(false);
  public showWarning$ = this.showWarningSubject.asObservable();

  private listenersActive = true;
  private listenersAdded = false;

  private countdownSubject = new BehaviorSubject<number>(60);
  public countdown$ = this.countdownSubject.asObservable();

  private resetTimerHandler = () => this.resetTimer();


  constructor(
    private ngZone: NgZone,
    private _authService: AuthService,
    private _translate: TranslateService,
    private _notificationService: NotificationService
  ) { }

  init() {
    this.resetTimer();
    this.addActivityListeners();
  }

 

  destroy() {
    clearTimeout(this.timeoutId);
    clearTimeout(this.warningTimeoutId);
    clearInterval(this.countdownIntervalId);
    this.removeActivityListeners();
    this.showWarningSubject.next(false);
  }

  private enableActivityListeners() {
    if (!this.listenersActive) {
      this.listenersActive = true;
      this.addActivityListeners();
    }
  }

  private disableActivityListeners() {
    if (this.listenersActive) {
      this.listenersActive = false;
      this.removeActivityListeners();
    }
  }

  resumeSession() {
    this.showWarningSubject.next(false);
    clearTimeout(this.warningTimeoutId);
    clearInterval(this.countdownIntervalId);
    this.enableActivityListeners();
    this.resetTimer();
  }

  forceLogout() {
    this.handleLogout();
  }


  private addActivityListeners() {
    if (this.listenersActive && !this.listenersAdded) {
      this.listenersAdded = true;
      // Use non-capturing listeners to avoid interfering with normal event handling
      this.events.forEach(event =>
        window.addEventListener(event, this.resetTimerHandler, { passive: true })
      );
      // Add visibility change listener to detect tab switching
      document.addEventListener('visibilitychange', this.resetTimerHandler, { passive: true });
    }
  }

  private removeActivityListeners() {
    if (this.listenersAdded) {
      this.listenersAdded = false;
      this.events.forEach(event =>
        window.removeEventListener(event, this.resetTimerHandler)
      );
      // Remove visibility change listener
      document.removeEventListener('visibilitychange', this.resetTimerHandler);
    }
  }

  private resetTimer() {
    clearTimeout(this.timeoutId);
    clearTimeout(this.warningTimeoutId);
    this.showWarningSubject.next(false);

    // Run timeout outside Angular zone to prevent unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => {
        this.ngZone.run(() => this.showWarning());
      }, this.inactivityTimeout);
    });
  }

  private showWarning() {
    this.showWarningSubject.next(true);
    this.countdownSubject.next(60);

    this.disableActivityListeners();

    // Run warning timeout outside Angular zone
    this.ngZone.runOutsideAngular(() => {
      this.warningTimeoutId = setTimeout(() => {
        this.ngZone.run(() => this.handleLogout());
      }, this.warningDuration);
    });

    this.startCountdown();
  }

  private startCountdown() {
    // Clear any existing countdown interval
    clearInterval(this.countdownIntervalId);

    this.countdownIntervalId = setInterval(() => {
      const current = this.countdownSubject.value;
      if (current <= 1) {
        clearInterval(this.countdownIntervalId);
        this.countdownSubject.next(0);
      } else {
        this.countdownSubject.next(current - 1);
      }
    }, 1000);
  }

  private handleLogout() {
    this.showWarningSubject.next(false);
    this.performLogout();
  }

  private performLogout() {
    localStorage.removeItem('auth');
    this._authService.logout();

    this._notificationService.warning(
      this._translate.instant('sessionExpired'),
      this._translate.instant('yourSessionHasExpiredDueToInactivity'),
      {
        position: 'top-right',
        showCloseButton: true,
        duration: 0,
        color: 'warning',
      }
    );
  }
}
